import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "risa-oasis-ui_v2";
import {
  firebaseApp,
  FirestoreService,
} from "../../../api/firebase/firestoreService";
import { processCsvForPharmacyPa } from "../../../api/postCall/processCsvApi";
import DragDropUpload from "../../../components/dragDropUpload/dragDropUpload";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../../store/UserGeneralInfoStore";
import { InfoIcon } from "../../../svg/info-icon";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { isProduction } from "../../../utils/runtimeEnv";

const FinalWorklistUpload = () => {
  const [createdAt] = useState(new Date().toISOString().replace(/[:.]/g, "-"));
  const [orgId, setOrgId] = useState<string>("");
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [orgName, setOrgName] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const navigate = useNavigate();

  const filePath = `medical_pa/${orgId}/final_worklist/${createdAt}.csv`;

  // Add this function to check and create Firebase Storage folder
  const checkAndCreateFirebaseStorageFolder = async (
    orgId: string,
  ): Promise<boolean> => {
    try {
      const { getStorage, ref, listAll } = await import("firebase/storage");
      const storage = getStorage(firebaseApp);

      // Define the folder path
      const folderPath = `medical_pa/${orgId}/final_worklist/`;
      const folderRef = ref(storage, folderPath);

      try {
        // Try to list contents of the folder
        await listAll(folderRef);
        logDataToConsole(`Folder exists: ${folderPath}`);
        return true;
      } catch (error: any) {
        // If folder doesn't exist, create it by uploading a placeholder file
        if (
          error.code === "storage/object-not-found" ||
          error.code === "storage/not-found"
        ) {
          logDataToConsole(`Folder doesn't exist, creating: ${folderPath}`);

          // Create a placeholder file to establish the folder structure
          const placeholderRef = ref(storage, `${folderPath}.placeholder`);
          const placeholderBlob = new Blob([""], { type: "text/plain" });

          const { uploadBytes } = await import("firebase/storage");
          await uploadBytes(placeholderRef, placeholderBlob);

          logDataToConsole(
            `Successfully created folder structure: ${folderPath}`,
          );
          return true;
        } else {
          throw error;
        }
      }
    } catch (error) {
      logError(
        error as Error,
        "Error checking/creating Firebase Storage folder",
      );
      return false;
    }
  };

  // Modified useEffect to include folder check
  useEffect(() => {
    const initializeFilePath = async () => {
      // Set the org ID after the organization setup is complete
      const orgIdForFetch = getOrgIdForFetchExternalWorklist();
      setOrgId(orgIdForFetch);

      // Check and create Firebase Storage folder if needed
      if (orgIdForFetch) {
        const folderExists =
          await checkAndCreateFirebaseStorageFolder(orgIdForFetch);
        if (!folderExists) {
          toast.error(ToastMessages.FOLDER_CREATION_FAILURE);
          logError(
            new Error("Failed to create Firebase Storage folder"),
            "initializeFilePath",
          );
        }
      }
    };
    initializeFilePath();
  }, []);

  useEffect(() => {
    UserStore.setItem(
      UserGeneralInfoContextKeys.PARENT_TABLE,
      window.location.pathname.split("/").slice(-1)[0],
    );
  }, []);

  useEffect(() => {
    const fetchOrgName = async () => {
      if (orgId) {
        setIsLoading(true);
        const healthCareFacility = await FirestoreService.getDocument(
          `/healthcare_facility/${orgId}`,
        );
        setIsLoading(false);
        setOrgName(healthCareFacility?.["Name"] ?? "");
      }
    };
    fetchOrgName();
  }, [orgId]);

  const handleUploadSuccess = async (urls: string[]) => {
    setUrl(urls[0]);
  };

  useEffect(() => {
    if (url && url !== "") {
      setShowUploadArea(false);
      toast.success(ToastMessages.FINAL_WORKLIST_UPLOAD_SUCCESS);
      triggerCsvProcessing();
    }
  }, [url]);

  useEffect(() => {
    return () => {
      setUrl("");
    };
  }, []);

  const triggerCsvProcessing = async () => {
    const selectedOrganization = getOrgIdForFetchExternalWorklist();
    const validGsPathForPayload = isProduction()
      ? `gs://prior--backen-prod-svc-u4g8.appspot.com`
      : `gs://prior-auth-portal-dev.appspot.com`;

    try {
      const payload = {
        gs_path: `${validGsPathForPayload}/medical_pa/${selectedOrganization}/final_worklist/${createdAt}.csv`,
        org_id: selectedOrganization,
      };
      processCsvForPharmacyPa(payload);
    } catch (error) {
      logError(error as Error, "Error triggering CSV processing");
      toast.error(ToastMessages.CSV_PROCESSING_FAILURE);
    }
  };

  return (
    <>
      <div className="final-worklist-upload-header self-start border-b border-primaryGray-16 py-2 text-h11 font-semibold">
        Final Worklist Upload
      </div>
      <div className="flex h-full w-full flex-col items-center p-4">
        {isLoading ? (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <LoaderMessage message="Loading organization name..." />
          </div>
        ) : (
          <>
            {" "}
            <div className="warning-info mb-4 w-[60%] rounded-md border border-secondaryYellow-5 bg-secondaryYellow-11 p-4">
              <div className="warning-info--header mb-3 flex items-center gap-2 text-h11 font-semibold text-secondaryYellow-3">
                <InfoIcon
                  height={"1.5rem"}
                  width={"1.5rem"}
                  stroke={"#857a00"}
                />
                Worksheet uploading details
              </div>
              <div className="warning-info--text text-small">
                You are going to upload a worksheet for the following
                organization:{" "}
                <span className="font-semibold italic">{orgName}</span>
              </div>
              <br />
              <div className="warning-info--text text-small">
                If you are uploading a worksheet for a different organization,
                please select the correct organization from the dropdown in{" "}
                <strong>Profile</strong> section under <strong>Settings</strong>{" "}
                Tab.
              </div>
              <div className="warning--info--actions mt-3 flex justify-end gap-2">
                <Button
                  disabled={!orgId || showUploadArea}
                  children={"Upload Worksheet"}
                  onClick={() => {
                    setShowUploadArea(true);
                  }}
                  buttonType={"primary"}
                  size={"small"}
                />
                <Button
                  disabled={false}
                  children={"Change Organization"}
                  onClick={() => {
                    navigate("/settings");
                  }}
                  buttonType={"secondary"}
                  size={"small"}
                />
              </div>
            </div>
          </>
        )}
        {orgId && showUploadArea && (
          <DragDropUpload
            id="payer-excel-upload"
            subText="Drag and drop or click to upload a new CSV file (.csv) for the final worklist. Only one file allowed."
            multipleFilesAllowed={false}
            allowedFileTypes={[".csv"]}
            maxFileSize={50 * 1024 * 1024}
            filePath={filePath}
            downloadedURL={handleUploadSuccess}
          />
        )}
      </div>
    </>
  );
};

export default FinalWorklistUpload;
